import { Alert, AlertTitle, Box, Button, Divider, Grid } from '@mui/material';
import { GenericDialog } from 'src/shared/components/organisms/genericDialog';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Schema } from 'yup';
import { DateHelper } from 'src/shared/helpers/dateHelper';
import { FormProvider, useForm } from 'react-hook-form';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { toast } from 'react-toastify';
import { useCallback, useEffect, useState } from 'react';
import FormLoaderWrapper from 'src/shared/components/organisms/formLoaderWrapper';
import { LoadingButton } from '@mui/lab';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { SelectableItem } from 'src/shared/models/gui/selectableItem';
import { InvoiceMissionTypeEnum } from 'src/shared/models/enums/invoiceMissionType.enum';
import { InvoiceGroups } from 'src/shared/components/form/invoiceGroups';
import { People } from '@mui/icons-material';
import { RecipientList } from 'src/modules/groupMessageEmail/components/recipientList';
import { createMemberFee } from 'src/shared/services/memberfeeService';
import { Member } from '../models/member';
import { MemberCreateInvoiceTableSum } from './memberCreateInvoiceTableSum';
import { MemberFeeRequest } from '../models/memberFeeRequest';

type MemberCreateInvoiceModalForMemberFeeProps = {
  member?: Member;
  members?: Member[];
  modalOpen: boolean;
  onClose: () => void;
  onSave: () => void;
};

export const createInvoiceMissionTypes: SelectableItem[] = [
  {
    label: 'invoice:createInvoice.invoiceMission.gift',
    value: InvoiceMissionTypeEnum.Gift,
  },
  {
    label: 'invoice:createInvoice.invoiceMission.purchase',
    value: InvoiceMissionTypeEnum.Purchase,
  },
  {
    label: 'invoice:createInvoice.invoiceMission.memberfee',
    value: InvoiceMissionTypeEnum.MemberFee,
  },
];

export const MemberCreateInvoiceModalForMemberFee = (props: MemberCreateInvoiceModalForMemberFeeProps) => {
  const { member = undefined, members = undefined, modalOpen, onClose, onSave } = props;
  const { t } = useTranslation(['common', 'invoice']);
  const isMounted = useIsMounted();
  const [loading, setLoading] = useState<boolean>(false);
  const [invoiceMissionId, setInvoiceMissionId] = useState<string | null>(null);
  const [invoiceTemplateId, setInvoiceTemplateId] = useState<number | undefined>();
  const [ownerId, setOwnerId] = useState<number>();
  const [totaltAmount, setTotaltAmount] = useState<number>(0);
  const hasOwnerId = useCallback(() => ownerId !== undefined && ownerId !== 0, [ownerId]);

  const getInvoiceMissionName = () => {
    if (!invoiceMissionId) return '';
    const selectedInvoiceMissionType = createInvoiceMissionTypes?.find((x) => x.value === invoiceMissionId);

    if (selectedInvoiceMissionType) {
      return t(selectedInvoiceMissionType.label);
    }

    return '';
  };

  const requiredMessage = t('common:validationMessages.required');

  const MemberFeeRequestSchema: Schema<MemberFeeRequest> = yup.object().shape({
    invoiceTemplateId: yup.number().required(requiredMessage),
    dueDate: yup.string().required(requiredMessage),
    memberIds: yup.array().required(),
  });

  const formMethods = useForm<MemberFeeRequest>({
    resolver: yupResolver(MemberFeeRequestSchema),
    mode: 'all',
    defaultValues: {
      invoiceTemplateId: InvoiceMissionTypeEnum.None,
      dueDate: DateHelper.getFSDate(dayjs(new Date()).add(1, 'month')),
      memberIds: [],
    },
  });

  const { handleSubmit, setValue } = formMethods;

  const onHandleClose = () => {
    onClose();
  };

  const onHandleSave = () => {
    onSave();
  };

  const onSubmit = useCallback(
    async (data: MemberFeeRequest) => {
      setLoading(true);
      try {
        await createMemberFee({
          dueDate: DateHelper.getFSDate(data.dueDate),
          invoiceTemplateId: invoiceTemplateId || 0,
          memberIds: data.memberIds,
        } as MemberFeeRequest);
        if (isMounted()) {
          onSave();
          toast.success(<>{t('invoice:createInvoice.success')}</>);
        }
      } catch (e) {
        if (isMounted()) ConsoleHelper.log(e);
      } finally {
        if (isMounted()) setLoading(false);
      }
    },
    [invoiceTemplateId, isMounted, onSave, t],
  );

  const handleSubmitClick = useCallback(() => {
    handleSubmit(onSubmit)();
  }, [handleSubmit, onSubmit]);

  useEffect(() => {
    if (modalOpen) {
      setInvoiceMissionId(null);
    }
  }, [modalOpen]);

  return (
    <GenericDialog
      open={modalOpen}
      onClose={onHandleClose}
      onSave={onHandleSave}
      dialogTitle={t('invoice:createInvoice.modalHeader')}
      dialogSubTitle={getInvoiceMissionName()}
      showDialogActions={false}
      padding={0}
      dialogContent={
        <FormLoaderWrapper loading={loading}>
          <FormProvider {...formMethods}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <Alert icon={false} severity='info' sx={{ m: 0 }}>
                {(member || members) && (
                  <AlertTitle sx={{ display: 'flex', alignItems: 'center' }}>
                    <People fontSize='small' sx={{ mr: 1 }} aria-hidden />
                    {t('groupMessage:selection.recipients')}&nbsp;<strong>({members && members.length ? members.length : 1})</strong>
                  </AlertTitle>
                )}
                {member && <RecipientList members={[member]} />}
                {members && <RecipientList members={members} />}
              </Alert>
              <Divider />
              <Box sx={{ p: 3 }}>
                <Box sx={{ mb: 3 }}>
                  <InvoiceGroups
                    callback={(ownId) => {
                      setOwnerId(ownId);
                    }}
                    placeholder={t('invoice:createInvoice.selectInvoiceGroup')}
                    emptyLabel={t('invoice:createInvoice.selectInvoiceGroup')}
                    autoSelect
                    ownerId={ownerId}
                  />
                  {!hasOwnerId() && (
                    <Alert severity='info' sx={{ mt: 2 }}>
                      <AlertTitle>{t('invoice:createInvoice.selectOwnerIdWarningTitle')}</AlertTitle>
                      {t('invoice:createInvoice.selectOwnerIdWarningText')}
                    </Alert>
                  )}
                </Box>

                {hasOwnerId() && (
                  <Grid container spacing={2}>
                    {members && (
                      <MemberCreateInvoiceTableSum
                        members={members}
                        ownerId={ownerId}
                        invoiceTemplateIdChanged={(x) => {
                          setInvoiceTemplateId(x);
                          setValue('invoiceTemplateId', x);
                        }}
                      />
                    )}
                    {member && (
                      <MemberCreateInvoiceTableSum
                        members={[member]}
                        ownerId={ownerId}
                        invoiceTemplateIdChanged={(x) => {
                          setInvoiceTemplateId(x);
                          setValue('invoiceTemplateId', x);
                        }}
                        setTotaltAmount={setTotaltAmount}
                      />
                    )}
                  </Grid>
                )}
                {hasOwnerId() && invoiceTemplateId && totaltAmount === 0 && member && (
                  <Alert severity='info' sx={{ mx: -3 }}>
                    <AlertTitle>{t('invoice:createInvoice.zeroFeeAlertTitle')}</AlertTitle>
                    {t('invoice:createInvoice.zeroFeeAlertText')}
                  </Alert>
                )}
              </Box>
              <Box>
                <Divider />
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1 }}>
                  <Button variant='outlined' sx={{ mr: 1 }} onClick={onHandleClose}>
                    {t('common:button.cancel')}
                  </Button>
                  <LoadingButton
                    loading={loading}
                    disabled={(invoiceMissionId === InvoiceMissionTypeEnum.MemberFee && !invoiceTemplateId) || (member && totaltAmount === 0)}
                    type='submit'
                    variant='contained'
                    disableElevation
                    onClick={handleSubmitClick}
                  >
                    {t('common:button.save')}
                  </LoadingButton>
                </Box>
              </Box>
            </form>
          </FormProvider>
        </FormLoaderWrapper>
      }
    />
  );
};
